import type { FaceDesign, RenderObject } from '@v2/contracts'
import { compose4, composeQuaternion4, type Mat4 } from '@v2/math'
import { createFaceCanvas, createLabelCanvas, faceKey } from './art'
import { createDisc, createPawn, createSlab, createTrain, type MeshGeometry } from './geometry'
import { createEnvironmentGeometry } from './environment-geometry'
import { createArtworkTexture, createImageCanvas, loadFaceImage } from './image-resource'
import type { Pipelines } from './pipelines'

export interface GpuMesh {
  vertex: GPUBuffer
  index: GPUBuffer
  indexCount: number
}
interface FaceTexture {
  texture: GPUTexture
  refs: number
  cancelImage?: () => void
}
export interface GpuObject {
  source: RenderObject
  model: Mat4
  uniform: GPUBuffer
  mainBinding: GPUBindGroup
  shadowBinding: GPUBindGroup
  mesh: GpuMesh
  faceKey: string
  backKey?: string
  signature: string
}

/** Explicitly owns scene meshes, per-object buffers, and ref-counted artwork. */
export class SceneResources {
  readonly objects = new Map<string, GpuObject>()
  private readonly meshes = new Map<RenderObject['shape'], GpuMesh>()
  private readonly textures = new Map<string, FaceTexture>()
  private readonly sampler: GPUSampler

  constructor(private readonly device: GPUDevice, private readonly pipelines: Pipelines) {
    this.sampler = device.createSampler({ label: 'Printed artwork sampler', magFilter: 'linear', minFilter: 'linear', mipmapFilter: 'linear', maxAnisotropy: 4 })
  }

  get textureCount(): number { return this.textures.size }
  get bufferCount(): number { return this.meshes.size * 2 + this.objects.size }

  sync(sources: readonly RenderObject[]): void {
    const active = new Set<string>()
    for (const source of sources) {
      if (active.has(source.id)) throw new Error(`Duplicate render object: ${source.id}`)
      active.add(source.id)
      if (source.size.some((dimension) => !(dimension > 0) || !Number.isFinite(dimension))) throw new Error(`Invalid dimensions for render object: ${source.id}`)
      if (source.position.some((value) => !Number.isFinite(value)) || !Number.isFinite(source.rotation)) throw new Error(`Invalid transform for render object: ${source.id}`)
      if (source.orientation && (source.orientation.some(value => !Number.isFinite(value)) || Math.hypot(...source.orientation) < 1e-10)) throw new Error(`Invalid orientation for render object: ${source.id}`)
      const key = artworkKey(source, source.face)
      const backKey = source.back ? artworkKey(source, source.back) : undefined
      let object = this.objects.get(source.id)
      if (!object) {
        const uniform = this.device.createBuffer({ label: 'Object transform/material', size: 176, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST })
        const texture = this.acquireFace(source, key)
        const backTexture = backKey ? this.acquireFace(source, backKey, source.back) : texture
        object = {
          source, model: objectTransform(source), uniform, faceKey: key, backKey, signature: '',
          mesh: this.getMesh(source.shape),
          mainBinding: this.mainBinding(uniform, texture, backTexture),
          shadowBinding: this.device.createBindGroup({ layout: this.pipelines.shadowObjectLayout, entries: [{ binding: 0, resource: { buffer: uniform } }] }),
        }
        this.objects.set(source.id, object)
      } else if (object.faceKey !== key || object.backKey !== backKey) {
        // Acquire both next faces before releasing either previous face: a flip
        // can swap front/back artwork without canceling and reloading it.
        const texture = object.faceKey === key ? this.textures.get(key)!.texture : this.acquireFace(source, key)
        const backTexture = !backKey ? texture : object.backKey === backKey ? this.textures.get(backKey)!.texture : this.acquireFace(source, backKey, source.back)
        object.mainBinding = this.mainBinding(object.uniform, texture, backTexture)
        if (object.faceKey !== key) this.releaseFace(object.faceKey)
        if (object.backKey && object.backKey !== backKey) this.releaseFace(object.backKey)
        object.faceKey = key
        object.backKey = backKey
      }
      object.source = source
      object.mesh = this.getMesh(source.shape)
      const signature = JSON.stringify([source.position, source.rotation, source.orientation, source.layer, source.size, source.color, source.surface, source.roughness, source.interactive, source.highlighted, source.dimmed, source.paymentEmphasis, source.decorative, source.shape, Boolean(source.face), Boolean(source.back)])
      if (object.signature !== signature) {
        object.signature = signature
        this.updateUniform(object)
      }
    }
    for (const [id, object] of this.objects) {
      if (!active.has(id)) {
        object.uniform.destroy()
        this.releaseFace(object.faceKey)
        if (object.backKey) this.releaseFace(object.backKey)
        this.objects.delete(id)
      }
    }
  }

  private getMesh(shape: RenderObject['shape']): GpuMesh {
    let mesh = this.meshes.get(shape)
    if (mesh) return mesh
    const geometry: MeshGeometry = createEnvironmentGeometry(shape) ?? (shape === 'disc' ? createDisc() : shape === 'pawn' ? createPawn() : shape === 'train' ? createTrain() : shape === 'board' ? createSlab(0.003) : createSlab())
    const vertex = this.device.createBuffer({ label: `${shape} vertices`, size: geometry.vertices.byteLength, usage: GPUBufferUsage.VERTEX, mappedAtCreation: true })
    new Float32Array(vertex.getMappedRange()).set(geometry.vertices)
    vertex.unmap()
    const index = this.device.createBuffer({ label: `${shape} indices`, size: geometry.indices.byteLength, usage: GPUBufferUsage.INDEX, mappedAtCreation: true })
    new Uint32Array(index.getMappedRange()).set(geometry.indices)
    index.unmap()
    mesh = { vertex, index, indexCount: geometry.indices.length }
    this.meshes.set(shape, mesh)
    return mesh
  }

  private acquireFace(source: RenderObject, key: string, design = source.face): GPUTexture {
    const existing = this.textures.get(key)
    if (existing) { existing.refs++; return existing.texture }
    const canvas = design ? source.shape === 'card' ? createFaceCanvas(design) : createLabelCanvas(design, source.size[0] / source.size[2]) : null
    const texture = createArtworkTexture(this.device, canvas)
    const face: FaceTexture = { texture, refs: 1 }
    this.textures.set(key, face)
    if (design?.image) {
      const aspect = source.size[0] / source.size[2]
      const background = design.background
      face.cancelImage = loadFaceImage(design.image, image => {
        if (this.textures.get(key) !== face || face.refs <= 0) return
        const artwork = createImageCanvas(image, aspect, background, Math.min(2048, this.device.limits.maxTextureDimension2D), design.imageRotation)
        const replacement = createArtworkTexture(this.device, artwork)
        try {
          const bindings = [...this.objects.values()]
            .filter(object => object.faceKey === key || object.backKey === key)
            .map(object => ({ object, binding: this.mainBinding(
              object.uniform,
              object.faceKey === key ? replacement : this.textures.get(object.faceKey)!.texture,
              (object.backKey ?? object.faceKey) === key ? replacement : this.textures.get(object.backKey ?? object.faceKey)!.texture,
            ) }))
          const previous = face.texture
          face.texture = replacement
          for (const { object, binding } of bindings) object.mainBinding = binding
          previous.destroy()
        } catch (error) {
          replacement.destroy()
          throw error
        }
      })
    }
    return texture
  }

  private releaseFace(key: string): void {
    const face = this.textures.get(key)
    if (!face) return
    face.refs--
    if (face.refs <= 0) {
      face.cancelImage?.()
      face.texture.destroy()
      this.textures.delete(key)
    }
  }

  private mainBinding(uniform: GPUBuffer, texture: GPUTexture, backTexture: GPUTexture): GPUBindGroup {
    const view = texture.createView()
    return this.device.createBindGroup({ label: 'Component material', layout: this.pipelines.objectLayout, entries: [
      { binding: 0, resource: { buffer: uniform } },
      { binding: 1, resource: view },
      { binding: 2, resource: this.sampler },
      { binding: 3, resource: backTexture === texture ? view : backTexture.createView() },
    ] })
  }

  private updateUniform(object: GpuObject): void {
    const source = object.source
    const values = new Float32Array(44)
    object.model = objectTransform(source)
    values.set(object.model)
    // Inverse-transpose for translation × orthonormal rotation × scale.
    const inverseScale = [1 / source.size[0], 1 / source.size[1], 1 / source.size[2]] as const
    values.set(source.orientation
      ? composeQuaternion4([0, 0, 0], source.orientation, inverseScale)
      : compose4([0, 0, 0], source.rotation, inverseScale), 16)
    values.set([...parseColor(source.color), source.surface === 'region' ? 0 : source.dimmed ? 0.45 : 1], 32)
    const roughness = source.roughness ?? (source.shape === 'card' ? 0.8 : 0.55)
    const metalness = roughness < 0.4 ? 0.55 : 0.015
    const paying = source.paymentEmphasis !== undefined && !source.decorative
    const shape = source.shape === 'disc' ? 2 : source.shape === 'pawn' ? 3 : 1
    const interaction = paying ? -shape : source.interactive && !source.decorative ? shape : 0
    values.set([roughness, metalness, source.face ? 1 : 0, paying ? Math.max(0, Math.min(1, source.paymentEmphasis!)) : interaction && source.highlighted ? 1 : 0], 36)
    values.set([(source.shape === 'slab' || source.shape === 'board') && roughness >= 0.9 ? 1 : 0, source.layer === 'foreground' ? 1 : 0, source.back ? 1 : 0, interaction], 40)
    this.device.queue.writeBuffer(object.uniform, 0, values.buffer)
  }

  dispose(): void {
    for (const object of this.objects.values()) object.uniform.destroy()
    for (const mesh of this.meshes.values()) { mesh.vertex.destroy(); mesh.index.destroy() }
    for (const face of this.textures.values()) { face.cancelImage?.(); face.texture.destroy() }
    this.objects.clear(); this.meshes.clear(); this.textures.clear()
  }
}

function objectTransform(source: RenderObject): Mat4 {
  return source.orientation
    ? composeQuaternion4(source.position, source.orientation, source.size)
    : compose4(source.position, source.rotation, source.size)
}

function artworkKey(source: RenderObject, design: FaceDesign | undefined): string {
  return design ? `${source.shape}:${(source.size[0] / source.size[2]).toFixed(3)}:${faceKey(design)}` : '__white'
}

/** Contract colors are portable CSS hex strings; unsupported values fail clearly. */
export function parseColor(color: string): readonly [number, number, number] {
  const raw = color.startsWith('#') ? color.slice(1) : color
  const expanded = raw.length === 3 ? raw.split('').map((digit) => digit + digit).join('') : raw
  if (!/^[\da-fA-F]{6}$/.test(expanded)) throw new Error(`Unsupported material color "${color}". Use #RGB or #RRGGBB.`)
  return [parseInt(expanded.slice(0, 2), 16) / 255, parseInt(expanded.slice(2, 4), 16) / 255, parseInt(expanded.slice(4, 6), 16) / 255]
}
